import axios from 'axios';
import Cookies from 'js-cookie';

const BASE_URL = import.meta.env.VITE_BASE_URL;

// Inject JWT from cookie when present so PartialContentFilter sees admin / paying users.
const buildAuthHeaders = (extra = {}) => {
  const token = Cookies.get('jwtToken');
  return {
    'Content-Type': 'application/json',
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};
const AWARD_NOTICE_LIST_API_URL = `${BASE_URL}/api/AwardNoticeListWeb/GetAwardNoticeListAsync`;
const AWARD_NOTICE_DETAILS_API_URL = `${BASE_URL}/api/AwardNoticeListWeb/GetAwardNoticeDetail`;
const AWARD_NOTICE_CONTRACTS_API_URL = `${BASE_URL}/api/AwardNoticeListWeb/GetAwardNoticeContracts`;

/**
 * Obține lista anunturi de atribuire (Contract Award Notices) cu paginare
 * @param {Object} filters - Filtrele de căutare (startDate, endDate, pageIndex, pageSize)
 * @returns {Promise<Object>} - Lista de anunturi de atribuire
 */
export const fetchAwardNoticeList = async (filters = {}) => {
  const {
    auctionId,
    startDate,
    endDate,
    pageIndex = 0,
    pageSize = 10,
  } = filters;

  try {
    const params = {
      pageIndex,
      pageSize,
    };

    if (auctionId) {
      params.auctionId = auctionId;
    }

    if (startDate) {
      params.startDate = startDate;
    }

    if (endDate) {
      params.endDate = endDate;
    }

    const response = await axios.get(AWARD_NOTICE_LIST_API_URL, {
      params,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Răspunsul API are structura { results, totalResults, pageIndex, pageSize, pageCount }
    return {
      results: response.data.results || [],
      totalResults: response.data.totalResults || 0,
      pageIndex: response.data.pageIndex || 0,
      pageSize: response.data.pageSize || 10,
      pageCount: response.data.pageCount || 0,
    };
  } catch (error) {
    console.error(
      'Eroare la obținerea listei anunturi de atribuire:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Obține detaliile unui anunț de atribuire specific
 * @param {string} noticeNo - Numărul anunțului de atribuire (e.g., CAN1161889, SCNA1120939)
 * @returns {Promise<Object>} - Detalii despre anunț
 */
export const fetchAwardNoticeDetail = async (id) => {
  if (!id) {
    throw new Error('ID-ul anunțului de atribuire este obligatoriu.');
  }

  try {
    const response = await axios.get(
      `${AWARD_NOTICE_DETAILS_API_URL}/${id}`,
      {
        headers: buildAuthHeaders(),
      },
    );

    // New API format: { rawJson: "stringified JSON", collectionName, auctionId, auctionExtension }
    // Parse rawJson if it exists, otherwise return data as-is for backward compatibility
    if (response.data?.rawJson) {
      try {
        const parsedData = JSON.parse(response.data.rawJson);

        // Normalize PascalCase keys to camelCase for frontend compatibility
        const normalizedData = {
          isPartial: response.data.isPartial || response.data.IsPartial || parsedData.IsPartial || false,
          generalInfo: parsedData.GeneralInfo || parsedData.generalInfo,
          canForView: parsedData.CanForView || parsedData.canForView,
          lotsList: parsedData.LotsList || parsedData.lotsList,
          noticeContracts:
            parsedData.NoticeContracts || parsedData.noticeContracts,
          extension: parsedData.Extension || parsedData.extension,
          rawJson: parsedData.rawJson,
          _metadata: {
            collectionName: response.data.collectionName,
            auctionId: response.data.auctionId,
            auctionExtension: response.data.auctionExtension,
          },
        };

        return normalizedData;
      } catch (parseError) {
        console.error('Failed to parse rawJson:', parseError);
        throw new Error('Răspunsul API conține date JSON invalide');
      }
    }

    return response.data;
  } catch (error) {
    console.error(
      'Eroare la obținerea detaliilor anunțului de atribuire:',
      error.response?.data || error.message,
    );
    throw error;
  }
};

/**
 * Obține contractele pentru un anunț de atribuire specific
 * @param {string} noticeNo - Numărul anunțului de atribuire (e.g., CAN1161889, SCNA1102354)
 * @returns {Promise<Object>} - Lista de contracte
 */
export const fetchAwardNoticeContracts = async (noticeNo) => {
  if (!noticeNo) {
    throw new Error('Numărul anunțului de atribuire este obligatoriu.');
  }

  try {
    // Extract numeric ID from noticeNo (works for all procedure types)
    // CAN1161889 -> 1161889, SCNA1102354 -> 1102354, etc.
    const caNoticeId = noticeNo.match(/\d+/)?.[0];

    if (!caNoticeId) {
      throw new Error(
        'ID-ul numeric din anunț nu a putut fi extras din: ' + noticeNo,
      );
    }

    // Trimite full noticeNo ca query parameter pentru ca backend-ul să știe prefixul original
    const response = await axios.get(
      `${AWARD_NOTICE_CONTRACTS_API_URL}/${caNoticeId}`,
      {
        params: {
          noticeNo: noticeNo, // SCNA1102354, CAN1161889, etc.
        },
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error(
      'Eroare la obținerea contractelor anunțului de atribuire:',
      error.response?.data || error.message,
    );
    throw error;
  }
};
